import React from 'react';
import SearchableUserDropdown from '../../Common/SearchableUserDropdown';

const formatAmount = (value) => new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
}).format(value ?? 0);

/**
 * One amount band of the purchase order approval matrix.
 *
 * Only the upper limit is editable. The lower limit is always the previous
 * band's upper limit (or 0 for the first band), so the ladder can never develop
 * a gap or an overlap from the UI.
 */
const POApprovalTierRow = ({
  tier,
  index,
  isLast,
  canRemove,
  disabled,
  errors = {},
  currency = 'LKR',
  onLabelChange,
  onMaxChange,
  onApproverChange,
  onRemoveApprover,
  onRemove
}) => {
  const maxError = errors[`tier_${index}_max`];
  const approversError = errors[`tier_${index}_approvers`];

  const rangeLabel = isLast
    ? `${currency} ${formatAmount(tier.minAmount)} and above`
    : `${currency} ${formatAmount(tier.minAmount)} – ${formatAmount(tier.maxAmount)}`;

  // Always render one empty slot so an approver can be added without a button.
  const approverSlots = [...(tier.approvers || []), { user: '', userName: '' }];

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <div className="flex items-start justify-between gap-4 px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
        <div>
          <p className="text-sm font-medium text-gray-900">
            Level {index + 1}
            {tier.label ? <span className="text-gray-500 font-normal"> · {tier.label}</span> : null}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">{rangeLabel}</p>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            disabled={disabled}
            className="text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
          >
            Remove
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={tier.label || ''}
              onChange={(e) => onLabelChange(index, e.target.value)}
              disabled={disabled}
              placeholder="e.g. Standard"
              maxLength={100}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              From ({currency})
            </label>
            <input
              type="text"
              value={formatAmount(tier.minAmount)}
              readOnly
              disabled
              title="Set by the previous level's upper limit"
              className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Up to and including ({currency})
            </label>
            {isLast ? (
              <input
                type="text"
                value="No limit"
                readOnly
                disabled
                className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
              />
            ) : (
              <input
                type="number"
                min="0"
                step="any"
                value={tier.maxAmount ?? ''}
                onChange={(e) => onMaxChange(index, e.target.value)}
                disabled={disabled}
                className={`w-full rounded-md border px-3 py-2 text-sm focus:ring-1 disabled:bg-gray-50
                  ${maxError
                    ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'}`}
              />
            )}
            {maxError && <p className="mt-1 text-xs text-red-600">{maxError}</p>}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Approvers
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Any one of these users can approve a purchase order in this range.
          </p>

          <div className="space-y-2">
            {approverSlots.map((approver, approverIndex) => {
              const isExisting = approverIndex < (tier.approvers || []).length;
              return (
                <div key={approver.user || `new-${approverIndex}`} className="flex items-center gap-2">
                  <div className="flex-1">
                    <SearchableUserDropdown
                      value={approver.user}
                      displayLabel={approver.userName}
                      onChange={(userId, displayName) => onApproverChange(index, approverIndex, userId, displayName)}
                      disabled={disabled}
                      error={!!approversError && !isExisting}
                      placeholder={isExisting ? 'Select approver' : 'Add an approver'}
                    />
                  </div>
                  {isExisting && (
                    <button
                      type="button"
                      onClick={() => onRemoveApprover(index, approverIndex)}
                      disabled={disabled}
                      title="Remove approver"
                      className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-50"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {approversError && <p className="mt-1 text-xs text-red-600">{approversError}</p>}
        </div>
      </div>
    </div>
  );
};

export default POApprovalTierRow;
